import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ShieldCheck, AlertCircle, CheckCircle, Loader2, Sparkles, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Modul G — Preview-Konfigurator (Ü18) · Phase 1a Enrollment-Landing.
 *
 * State-Machine (aus Backend):
 *  NOT_STARTED  → Opt-In-Button
 *  OPTED_IN     → Consent-Card
 *  CONSENT_GIVEN→ AVS-Redirect
 *  AVS_PENDING  → Wait-State
 *  ACTIVE       → CTA zu Subject/Preview (kommt Phase 1b)
 *  SUSPENDED    → Admin-Kontakt-Hinweis
 *  REVOKED      → Re-Enroll-Option
 */
const CURRENT_CONSENT_VERSION = "preview_consent_v1";

export default function ModulGPreviewEnrollmentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/modul-g/enrollment");
      setState(data);
    } catch (e) {
      alert(e.response?.data?.detail || "Konnte Enrollment-Status nicht laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) { navigate("/login?next=/studio/preview"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const optIn = async () => {
    setBusy(true);
    try {
      await api.post("/modul-g/enrollment/opt-in");
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Opt-In fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const submitConsent = async () => {
    if (!consentChecked) return;
    setBusy(true);
    try {
      await api.post("/modul-g/enrollment/consent", {
        consent_version: CURRENT_CONSENT_VERSION,
        accepted: true,
      });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail?.message || e.response?.data?.detail || "Consent fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm(t("modul_g_enrollment.confirm_revoke"))) return;
    setBusy(true);
    try {
      await api.post("/modul-g/enrollment/revoke");
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Widerruf fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const startAvs = () => {
    navigate("/profile/access?next=/studio/preview");
  };

  if (loading) {
    return <div data-testid="modul-g-loading" className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#666]" /></div>;
  }

  const enroll = state.enrollment;
  const req = state.requirements;
  const status = enroll.status;

  return (
    <div data-testid="modul-g-enrollment-page" className="min-h-screen bg-white text-[#0A0A0A]">
      <div className="sticky top-0 flex items-center gap-3 h-14 px-4 border-b border-[#E5E5E5] bg-white">
        <button onClick={() => navigate("/studio")} className="text-[#0A0A0A]" data-testid="mg-back-btn"><ArrowLeft size={20} /></button>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#999]">{t("modul_g_enrollment.modul_g_18")}</p>
          <h1 className="text-base font-bold">{t("modul_g_enrollment.page_title")}</h1>
        </div>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Status Card */}
        <div data-testid="mg-status-card" className="border border-[#E5E5E5] p-4 rounded-none">
          <p className="text-[10px] uppercase tracking-wider text-[#999] mb-2">Aktueller Status</p>
          <div className="flex items-center gap-2">
            {status === "ACTIVE" ? <CheckCircle className="text-emerald-600" size={20} /> :
             status === "REVOKED" || status === "SUSPENDED" ? <XCircle className="text-red-500" size={20} /> :
             <AlertCircle className="text-amber-500" size={20} />}
            <span data-testid="mg-status-label" className="text-sm font-semibold">{state.status_label}</span>
          </div>
        </div>

        {/* Requirements Checklist */}
        <div className="border border-[#E5E5E5] p-4 space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-[#999] mb-2">Voraussetzungen</p>
          <RequirementRow ok={req.seller_capability} label="Aktiver STX-Seller (ST_CREATOR)" testId="req-seller" />
          <RequirementRow ok={req.consent} label="Consent akzeptiert (preview_consent_v1)" testId="req-consent" />
          <RequirementRow ok={req.avs_verified} label={t("modul_g_enrollment.avs_label")} testId="req-avs" />
        </div>

        {/* Action based on state */}
        {status === "NOT_STARTED" && (
          <div className="border border-[#0A0A0A] p-4 space-y-3">
            <div className="flex items-start gap-2">
              <Sparkles size={18} className="text-amber-500 shrink-0 mt-0.5" />
              <p className="text-sm">
                Aktiviere die Preview-Funktion: Käufer sehen Vorschau-Bilder & Videos deiner Artikel in verschiedenen Trage-Zuständen (State-Preview).
              </p>
            </div>
            <button data-testid="mg-opt-in-btn" onClick={optIn} disabled={busy}
              className="w-full h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-medium disabled:opacity-50">
              {busy ? "…" : "Anmelden (opt-in)"}
            </button>
          </div>
        )}

        {status === "OPTED_IN" && !req.consent && (
          <div className="border border-amber-500 p-4 space-y-3">
            <div className="flex items-start gap-2">
              <ShieldCheck size={18} className="text-amber-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold mb-1">Consent erforderlich</p>
                <p className="text-xs text-[#555]">Bitte bestätige, dass du der Preview-Nutzung nach den geltenden Bedingungen zustimmst (Version {CURRENT_CONSENT_VERSION}).</p>
              </div>
            </div>
            <label data-testid="mg-consent-checkbox" className="flex items-start gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} className="mt-0.5" />
              <span>Ich stimme der Preview-Nutzung meiner Assets für die Erzeugung von State-Preview-Bildern und -Videos zu. Ich bestätige, dass ich Ü18 bin (via AVS zu verifizieren).</span>
            </label>
            <button data-testid="mg-consent-submit-btn" onClick={submitConsent} disabled={busy || !consentChecked}
              className="w-full h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-medium disabled:opacity-50">
              {busy ? "…" : "Consent bestätigen"}
            </button>
          </div>
        )}

        {status === "OPTED_IN" && req.consent && !req.seller_capability && (
          <div className="border border-red-500 p-4">
            <p className="text-sm">
              Du benötigst zunächst den STX-Seller-Status (ST_CREATOR).{" "}
              <button onClick={() => navigate("/profile/access")} className="underline">Zum Onboarding</button>
            </p>
          </div>
        )}

        {status === "AVS_PENDING" && (
          <div className="border border-amber-500 p-4 space-y-3">
            <p className="text-sm">{t("modul_g_enrollment.last_step")}</p>
            <button data-testid="mg-start-avs-btn" onClick={startAvs}
              className="w-full h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-medium">
              AVS starten
            </button>
          </div>
        )}

        {status === "ACTIVE" && (
          <div className="border border-emerald-600 p-4 space-y-3">
            <div className="flex items-center gap-2 text-emerald-600">
              <CheckCircle size={18} /> <span className="text-sm font-semibold">Preview aktiv</span>
            </div>
            <p className="text-xs text-[#555]">
              Nächster Schritt: Motiv anlegen & Base-Bild (+ optional Base-Video) hochladen — anschließend Preview-Request stellen.
            </p>
            <button data-testid="mg-goto-subjects-btn" onClick={() => navigate("/studio/preview/subjects")}
              className="w-full h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-medium">
              Motive & Base-Assets verwalten
            </button>
            <button data-testid="mg-goto-requests-btn" onClick={() => navigate("/studio/preview/requests")}
              className="w-full h-11 rounded-full border border-[#0A0A0A] text-[#0A0A0A] text-sm font-medium">
              Preview-Requests
            </button>
            <button data-testid="mg-revoke-btn" onClick={revoke} disabled={busy}
              className="w-full h-10 rounded-full border border-red-500 text-red-500 text-sm font-medium disabled:opacity-50">
              Preview widerrufen
            </button>
          </div>
        )}

        {status === "SUSPENDED" && (
          <div className="border border-red-500 p-4">
            <p className="text-sm font-semibold text-red-500">Enrollment suspendiert</p>
            <p className="text-xs text-[#555] mt-1">{t("modul_g_enrollment.contact_support")} <a href="mailto:support@stilltouched.com" className="underline">support@stilltouched.com</a></p>
          </div>
        )}

        {status === "REVOKED" && (
          <div className="border border-[#E5E5E5] p-4 space-y-3">
            <p className="text-sm">{t("modul_g_enrollment.revoked")}</p>
            <button data-testid="mg-reenroll-btn" onClick={optIn} disabled={busy}
              className="w-full h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-medium disabled:opacity-50">
              Erneut anmelden
            </button>
          </div>
        )}

        <p className="text-[10px] text-[#999] text-center px-4">
          {t("modul_g_enrollment.u18_note")}
        </p>
      </div>
    </div>
  );
}

function RequirementRow({ ok, label, testId }) {
  return (
    <div data-testid={testId} className="flex items-center gap-2 text-sm">
      {ok ? <CheckCircle size={14} className="text-emerald-600" /> : <XCircle size={14} className="text-[#999]" />}
      <span className={ok ? "text-[#0A0A0A]" : "text-[#666]"}>{label}</span>
    </div>
  );
}
